import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { formatPreferredCurrency } from '@/hooks/usePreferredCurrencyFormat';
import { PaymentScheduleStage } from '@/ui/components/paymentScheduleStageCards/PaymentScheduleStage';
import {
  ProposalDepositStageCard,
  ProposalInstalmentStageCard,
} from '@/ui/components/paymentScheduleStageCards/ProposalPaymentScheduleStageCards';
import { toValuableStoredPaymentSchedule } from '@/ui/components/paymentScheduleStageCards/stageHelpers';
import { usePaymentScheduleStages } from '@/ui/components/paymentScheduleStageCards/usePaymentScheduleStages';
import { Card, FieldError, Text } from '@payaca/ambrosia';
import {
  getDerivedPaymentScheduleStages,
  getStoredStagesWithGrossValues,
  withExactFullAllocation,
} from '@payaca/helpers/paymentScheduleHelper';
import { type StoredPaymentSchedule } from '@payaca/types/payment-schedule';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { type InvoicedStage } from './editableChangeProposalPaymentSchedule';

type Props = {
  /** The Project's total if the change proposal is accepted, in minor units. */
  futureProjectValue: number;
  /** Stages already invoiced, which can't be changed or removed. */
  invoicedStages: InvoicedStage[];
};

const ChangeProposalPaymentScheduleEditor: FC<Props> = (props) => {
  const { futureProjectValue, invoicedStages } = props;

  const {
    fields,
    stages,
    allocatedPercentage,
    remainingPercentage,
    addStage,
    removeStage,
    setFirstStageIsDeposit,
    stagesErrorMessage,
  } = usePaymentScheduleStages();

  const invoicedStageIndices = new Set(
    invoicedStages.map(({ stageIndex }) => stageIndex)
  );

  const valuableSchedule = withExactFullAllocation(
    toValuableStoredPaymentSchedule(stages)
  );
  const stageAmounts = getStoredStagesWithGrossValues(
    valuableSchedule,
    futureProjectValue
  ).map(({ value }) => value ?? 0);
  const amountsCoveredByDeposit = getAmountsCoveredByDeposit({
    paymentSchedule: valuableSchedule,
    grossTotal: futureProjectValue,
    stageAmounts,
    depositInvoice:
      invoicedStages.find(({ stageIndex }) => stageIndex === 0) ?? null,
  });

  if (!fields.length) {
    return <PaymentScheduleStage.AddScheduleButton onClick={addStage} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <PaymentScheduleStage.Header
          allocatedPercentage={allocatedPercentage}
        />
        {invoicedStages.length > 0 && (
          <Text level="supporting">
            Invoiced stages can&apos;t be changed or removed. You can still edit
            the other stages and add new ones.
          </Text>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {invoicedStages.length > 0 && (
          <InvoicedStagesCard
            invoicedStages={invoicedStages}
            futureProjectValue={futureProjectValue}
          />
        )}
        <div className="flex flex-col gap-4">
          {fields.map((field, index) => {
            if (invoicedStageIndices.has(index)) return null;
            const StageCard =
              stages[index]?.type === 'deposit'
                ? ProposalDepositStageCard
                : ProposalInstalmentStageCard;
            return (
              <StageCard
                key={field.id}
                index={index}
                proposalTotal={futureProjectValue}
                amount={stageAmounts[index] ?? 0}
                amountCoveredByDeposit={amountsCoveredByDeposit[index]}
                remainingPercentage={remainingPercentage}
                onDepositChange={
                  index === 0 ? setFirstStageIsDeposit : undefined
                }
                onRemove={() => removeStage(index)}
              />
            );
          })}
        </div>
        {stagesErrorMessage && <FieldError messages={[stagesErrorMessage]} />}
        <PaymentScheduleStage.AddStageButton
          remainingPercentage={remainingPercentage}
          onClick={addStage}
        />
        <PaymentScheduleStage.ExplainerField />
      </div>
    </div>
  );
};

/**
 * What a fixed deposit takes off each stage, before grants, to match the gross
 * amounts the stages show. A stage the deposit uses up has no derived stage.
 */
const getAmountsCoveredByDeposit = (args: {
  paymentSchedule: StoredPaymentSchedule;
  grossTotal: number;
  stageAmounts: number[];
  depositInvoice: { value: number } | null;
}): number[] => {
  const { paymentSchedule, grossTotal, stageAmounts, depositInvoice } = args;
  const derivedAmounts = new Map(
    getDerivedPaymentScheduleStages({
      paymentSchedule,
      grossTotal,
      grants: [],
      depositInvoice,
    }).flatMap((stage) =>
      stage.type === 'instalment'
        ? [[stage.storedStageIndex, stage.value] as const]
        : []
    )
  );

  return stageAmounts.map((amount, index) =>
    paymentSchedule.stages[index]?.type === 'instalment'
      ? amount - (derivedAmounts.get(index) ?? 0)
      : 0
  );
};

const InvoicedStagesCard: FC<{
  invoicedStages: InvoicedStage[];
  futureProjectValue: number;
}> = (props) => {
  const { invoicedStages, futureProjectValue } = props;
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const currency = regionPreferences?.currency;

  const formatAmount = (value: number) =>
    currency ? formatPreferredCurrency({ value, currency }) : '';
  const stillToInvoice =
    futureProjectValue -
    invoicedStages.reduce((total, stage) => total + stage.value, 0);

  return (
    <Card sizeVariant="sm">
      <Card.Body className="flex flex-col gap-1">
        <Text level="supporting">Already invoiced</Text>
        <div className="flex flex-col divide-y divide-gray-200">
          {invoicedStages.map((stage) => (
            <div
              key={stage.invoiceLabel}
              className="flex items-center gap-2 py-2"
            >
              <UntitledIcon name="lock-01.3" className="h-4 w-4 shrink-0" />
              <Text as="span">
                {stage.description} · {formatAmount(stage.value)} ·{' '}
                {stage.invoiceLabel}
              </Text>
            </div>
          ))}
          <div className="pt-2">
            <Text as="span">
              Still to invoice: <strong>{formatAmount(stillToInvoice)}</strong>
            </Text>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ChangeProposalPaymentScheduleEditor;
